import {
  Button,
  Flex,
  Modal,
  ScrollArea,
  Select,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback } from "react";

import { yesNoOptions } from "../../../constants.ts";
import { UpdateCustomerRiskMonitoringRemarksInput } from "../../../graphql/generated.ts";
import {
  RiskMonitoringRemarks,
  riskRecommendationOptions,
} from "../../../types/customer.ts";
import { booleanToString, stringToBoolean } from "../../../utils/boolean.ts";

interface Props {
  data: RiskMonitoringRemarks | null | undefined;
  opened?: boolean;
  onClose: () => void;
  onSubmit: (value: UpdateCustomerRiskMonitoringRemarksInput) => void;
}

export const CreateRiskMonitoringModal = ({
  data,
  opened = false,
  onClose,
  onSubmit,
}: Props) => {
  const form = useForm({
    initialValues: {
      priceAdjustment: booleanToString(data?.priceAdjustment, "formValue"),
      riskRecommendation: data?.riskRecommendation,
      notes: data?.notes,
    },
    validate: {},
    validateInputOnBlur: true,
  });

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }

    onSubmit({
      ...form.values,
      priceAdjustment: stringToBoolean(form.values.priceAdjustment, true),
    } as UpdateCustomerRiskMonitoringRemarksInput);
    handleClose();
  }, [form, handleClose, onSubmit]);

  return (
    <>
      <Modal
        opened={opened}
        title={data?.id ? "Update Risk Monitoring" : "Add Risk Monitoring"}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="md"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <Flex direction="column" gap={12}>
            <Select
              data={yesNoOptions}
              size="m"
              label="Price Adjustment"
              placeholder="Select Price Adjustment"
              clearable
              {...form.getInputProps("priceAdjustment")}
            />
            <Select
              data={riskRecommendationOptions}
              size="m"
              label="Risk Recommendation"
              placeholder="Select Risk Recommendation"
              clearable
              {...form.getInputProps("riskRecommendation")}
            />
            <Textarea
              label="Remarks"
              placeholder="Enter Remarks"
              autosize
              size="m"
              minRows={3}
              {...form.getInputProps("notes")}
            />
          </Flex>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid()}
              onClick={handleSubmit}
            >
              add
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
